'use client';

/**
 * Tracker components for server-rendered pages.
 *
 * These fire GA4 events on mount via useEffect + useRef guard to prevent
 * double-firing in React strict mode / fast-refresh.
 */

import {
  trackBeginCheckout,
  trackPurchase,
  trackViewItem,
  trackViewItemList,
} from '@lib/analytics/events';
import { lineItemToGA4Item } from '@lib/analytics/items';
import {
  hasTrackedPurchase,
  markPurchaseTracked,
} from '@lib/analytics/purchase-dedupe';
import type { GA4Item } from '@lib/analytics/types';
import { useEffect, useRef } from 'react';
import type { TrackBeginCheckoutVM, TrackPurchaseVM } from 'types/view-models';

/* ------------------------------------------------------------------ */
/*  TrackViewItem                                                      */
/* ------------------------------------------------------------------ */

export function TrackViewItem({ item }: { item: GA4Item }) {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    trackViewItem(item, item.price ?? 0, item.currency ?? 'GBP');
  }, [item]);

  return null;
}

/* ------------------------------------------------------------------ */
/*  TrackItemList                                                      */
/* ------------------------------------------------------------------ */

export const TrackItemList = ({
  items,
  listId,
  listName,
}: {
  items: GA4Item[];
  listId?: string;
  listName?: string;
}) => {
  const firedSignature = useRef<string | null>(null);

  useEffect(() => {
    if (items.length === 0) return;

    // Build a stable signature so we re-fire when the list actually changes
    // (e.g. pagination, sort, different category) but not on identity-equal re-renders.
    const signature = `${listId}:${items.map((i) => i.item_id).join(',')}`;
    if (firedSignature.current === signature) return;

    const track = () => {
      // Set signature inside the callback so that if cleanup cancels the
      // callback (e.g. React Strict Mode double-invoke or fast navigation),
      // the next effect run can reschedule correctly.
      firedSignature.current = signature;
      trackViewItemList(items, listId, listName);
    };

    // Defer analytics to idle so it doesn't block main-thread hydration (TBT).
    if (typeof requestIdleCallback !== 'undefined') {
      const cbId = requestIdleCallback(track, { timeout: 2000 });
      return () => cancelIdleCallback(cbId);
    }
    const timerId = setTimeout(track, 100);
    return () => clearTimeout(timerId);
  }, [items, listId, listName]);

  return null;
};

/* ------------------------------------------------------------------ */
/*  TrackBeginCheckout                                                 */
/* ------------------------------------------------------------------ */

export const TrackBeginCheckout = ({
  cart,
}: {
  cart: TrackBeginCheckoutVM;
}) => {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !cart.items?.length) return;
    fired.current = true;

    const items = cart.items.map((item) => lineItemToGA4Item(item));
    trackBeginCheckout(items, cart.total ?? 0, cart.currency_code ?? 'GBP');
  }, [cart]);

  return null;
};

/* ------------------------------------------------------------------ */
/*  TrackPurchase                                                      */
/* ------------------------------------------------------------------ */

// Cyclomatic complexity is the `??` defaulting of optional order fields —
// flat mapping, no branching logic to extract.
// fallow-ignore-next-line complexity
const firePurchase = (order: TrackPurchaseVM): void => {
  trackPurchase(
    order.id,
    order.total ?? 0,
    order.currency_code ?? 'GBP',
    order.tax_total ?? 0,
    order.shipping_total ?? 0,
    order.items,
    // Enhanced conversions user data
    {
      email: order.email ?? undefined,
      phone_number: order.shipping_address?.phone ?? undefined,
      first_name: order.shipping_address?.first_name ?? undefined,
      last_name: order.shipping_address?.last_name ?? undefined,
      street: order.shipping_address?.address_1 ?? undefined,
      city: order.shipping_address?.city ?? undefined,
      postal_code: order.shipping_address?.postal_code ?? undefined,
      country: order.shipping_address?.country_code ?? undefined,
    },
  );
};

export const TrackPurchase = ({ order }: { order: TrackPurchaseVM }) => {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    // Deduped by order id across page loads — a confirmation-page reload or
    // revisit must not re-fire `purchase` (it inflated revenue ~16%, MWP-899).
    if (hasTrackedPurchase(order.id)) return;
    fired.current = true;
    markPurchaseTracked(order.id);

    firePurchase(order);
  }, [order]);

  return null;
};
